import type { Role } from "../lib/roles";
import { ROLES } from "../lib/roles";

/**
 * Корпус статей справки (E16).
 *
 * ЕДИНЫЙ ИСТОЧНИК ТЕКСТА — каталог `docs/help/` в репозитории. Оттуда же экспортируются
 * памятки Д1/Д2 (E14): текст ровно один и разойтись физически не может — это и есть
 * требование «единого источника» из change request владельца.
 *
 * СТАТЬИ ПОПАДАЮТ В ПРИЛОЖЕНИЕ ПРИ СБОРКЕ, А НЕ ЧИТАЮТСЯ С ДИСКА. `import.meta.glob` с
 * `eager` разворачивается сборщиком в обычные импорты, поэтому в готовом бандле лежит уже
 * текст, а не путь к файлу. Это важно не «для скорости»: на площадке рядом с контейнером api
 * никакого `docs/` нет вовсе, и чтение с диска означало бы пустую справку в проде при зелёных
 * тестах локально. Проверяется тестом, который читает корпус, не касаясь файловой системы.
 *
 * Каталог лежит ВЫШЕ корня Vite (`apps/web`), поэтому в `vite.config.ts` он поимённо открыт в
 * `server.fs.allow`; репозиторий целиком там НЕ открывается — рядом лежат файлы окружения.
 */

const RAW_ARTICLES = import.meta.glob("../../../../docs/help/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

/** Статья справки. */
export interface HelpArticle {
  readonly id: string;
  readonly title: string;
  /** Тема оглавления: статьи одной темы идут одной группой. */
  readonly group: string;
  /**
   * Кому статья адресована. Пусто — статья общая (вход, пароль, интерфейс).
   *
   * В ИНТЕРФЕЙСЕ ЭТО ПОЛЕ НЕ ПОКАЗЫВАЕТСЯ ВООБЩЕ: фильтра по ролям нет (решение 22.08.2026),
   * подписи «кому адресована» тоже нет (решение 23.08.2026) — все статьи одинаково открыты всем.
   * **Удалять поле нельзя:** это сырьё для памяток ролей Д1/Д2 в E14, без него памятку не из
   * чего разложить. Сохранность закрыта тестом — иначе поле выглядит мёртвым и будет прибрано
   * первой же чисткой.
   */
  readonly roles: readonly Role[];
  /** Порядок внутри группы. */
  readonly order: number;
  /** Слова для поиска, которых может не быть в тексте («штраф» к статье о неустойке). */
  readonly keywords: readonly string[];
  /**
   * СЛУЖЕБНАЯ СТАТЬЯ — в справке не показывается никому (решение владельца 23.08.2026).
   *
   * Такая статья остаётся в каталоге `docs/help/`, потому что из неё собирается памятка роли в
   * E14 (ТЗ требует памятки на все шесть ролей, включая администратора), — но в продукте её нет
   * ни в оглавлении, ни в поиске, ни по прямому адресу. Основание: администратор — служебная
   * роль, и рядовому пользователю читать, что он умеет, незачем.
   *
   * Именно поэтому корпус разделён на два перечня: `HELP_CORPUS` (всё, для экспорта) и
   * `HELP_ARTICLES` (то, что видит человек). Одного перечня с фильтром по месту хватило бы ровно
   * до первого места, где фильтр забыли.
   */
  readonly service: boolean;
  /** Тело статьи в Markdown — без шапки. Разметка — подмножество, см. `markdown.tsx`. */
  readonly body: string;
}

/** Разбор шапки: `ключ: значение` между двумя строками `---`. */
function parseHeader(source: string, file: string): { head: Map<string, string>; body: string } {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(source);
  if (match === null) throw new Error(`Статья справки без шапки: ${file}`);
  const head = new Map<string, string>();
  for (const line of match[1]!.split(/\r?\n/)) {
    if (line.trim() === "") continue;
    const sep = line.indexOf(":");
    if (sep < 0) throw new Error(`Строка шапки без «ключ: значение» (${file}): ${line}`);
    head.set(line.slice(0, sep).trim(), line.slice(sep + 1).trim());
  }
  return { head, body: (match[2] ?? "").trim() };
}

/** Список через запятую; пустая строка — пустой список (а не список из одной пустоты). */
function splitList(value: string | undefined): string[] {
  if (value === undefined || value.trim() === "") return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v !== "");
}

function required(head: Map<string, string>, key: string, file: string): string {
  const value = head.get(key);
  if (value === undefined || value === "") throw new Error(`В шапке ${file} нет поля «${key}»`);
  return value;
}

const ROLE_SET = new Set<string>(ROLES);

function parseArticle(file: string, source: string): HelpArticle {
  const { head, body } = parseHeader(source, file);
  const id = required(head, "id", file);
  // Имя файла и идентификатор совпадают намеренно: адрес статьи (`/help/<id>`) должен искаться
  // в репозитории обычным поиском по имени файла, иначе правка «той самой статьи» превращается
  // в перебор.
  const expected = `${id}.md`;
  if (!file.endsWith(`/${expected}`)) {
    throw new Error(`Идентификатор «${id}» не совпадает с именем файла: ${file}`);
  }
  const roles = splitList(head.get("roles"));
  for (const role of roles) {
    if (!ROLE_SET.has(role)) throw new Error(`Неизвестная роль «${role}» в шапке ${file}`);
  }
  const order = Number(required(head, "order", file));
  if (!Number.isFinite(order)) throw new Error(`Поле «order» в ${file} — не число`);
  if (body === "") throw new Error(`Статья ${file} пуста`);
  // Пометка служебной пишется словом: `service: true`. Отсутствие поля — обычная статья.
  const service = head.get("service");
  if (service !== undefined && service !== "true" && service !== "false") {
    throw new Error(`Поле «service» в ${file} — только true или false`);
  }
  return {
    id,
    title: required(head, "title", file),
    group: required(head, "group", file),
    roles: roles as Role[],
    order,
    keywords: splitList(head.get("keywords")),
    service: service === "true",
    body,
  };
}

/**
 * ВЕСЬ корпус, включая служебные статьи. Отсюда E14 собирает памятки ролей — там нужны все
 * шесть, включая администратора. В интерфейсе этот перечень не используется НИГДЕ.
 */
export const HELP_CORPUS: readonly HelpArticle[] = Object.entries(RAW_ARTICLES)
  .map(([file, source]) => parseArticle(file, source))
  .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title, "ru"));

/** Статьи, которые видит человек: всё, кроме служебных. Оглавление, поиск и адрес — по нему. */
export const HELP_ARTICLES: readonly HelpArticle[] = HELP_CORPUS.filter((a) => !a.service);

/** Темы в порядке появления — чтобы оглавление не пересортировывалось по алфавиту тем. */
export function helpGroups(articles: readonly HelpArticle[] = HELP_ARTICLES): string[] {
  const seen: string[] = [];
  for (const article of articles) if (!seen.includes(article.group)) seen.push(article.group);
  return seen;
}

/**
 * Статья по адресу; `null` — такой статьи нет (устаревшая ссылка, план §5 п. 4).
 *
 * Служебная статья по прямому адресу тоже отдаёт `null`, и это не оплошность: спрятать её из
 * оглавления и оставить открытой по ссылке значило бы спрятать только от честного читателя.
 */
export function helpArticleById(id: string): HelpArticle | null {
  return HELP_ARTICLES.find((a) => a.id === id) ?? null;
}

/**
 * Поиск по корпусу — вхождение подстроки в заголовок, ТЕКСТ и ключевые слова. Морфологии нет
 * намеренно (план §8): корпус мал, а «умный» поиск на полутора десятках статей чаще мешает.
 */
export function searchArticles(
  query: string,
  articles: readonly HelpArticle[] = HELP_ARTICLES,
): HelpArticle[] {
  const needle = query.trim().toLocaleLowerCase("ru");
  if (needle === "") return [...articles];
  return articles.filter((a) =>
    [a.title, a.body, ...a.keywords].some((text) => text.toLocaleLowerCase("ru").includes(needle)),
  );
}

/**
 * Текст статьи без разметки — то, что человек прочитал бы глазами. Нужен и подписи в оглавлении,
 * и фрагменту поиска: оба показывают ТЕКСТ, а не исходник, и звёздочки с квадратными скобками в
 * них выглядели бы мусором.
 */
function plainText(markdown: string): string {
  return markdown
    .replace(/\[([^\]\n]+)\]\([^)\s]*\)/g, "$1") // ссылка → её подпись
    .replace(/\*\*/g, "") // выделение
    .replace(/^\s*(?:#{2,3}\s+|-\s+|\d+\.\s+)/gm, "") // маркеры заголовков и списков
    .replace(/\s+/g, " ")
    .trim();
}

/** Первый абзац статьи — короткая подпись в оглавлении. */
export function articleSummary(article: HelpArticle, limit = 160): string {
  const first =
    article.body
      .split(/\r?\n\s*\r?\n/)
      .map((block) => block.trim())
      .find((block) => block !== "" && !block.startsWith("#")) ?? "";
  const plain = plainText(first);
  return plain.length > limit ? `${plain.slice(0, limit - 1).trimEnd()}…` : plain;
}

/**
 * Фрагмент статьи вокруг найденного слова: что стоит ДО вхождения, само вхождение и что ПОСЛЕ.
 * Разбито на три части намеренно — подсветку рисует экран, а не эта функция: возвращать сюда
 * разметку значило бы решать за него, чем подсвечивать.
 *
 * `null` — вхождения в тексте нет вовсе. Так бывает законно: статью мог найти поиск по заголовку
 * или по ключевому слову («штраф» к статье о неустойке). Показывать в этом случае нечего, и экран
 * подставляет обычную подпись — но НЕ выдаёт её за найденное место.
 */
export interface Snippet {
  readonly before: string;
  readonly match: string;
  readonly after: string;
}

const CONTEXT_BEFORE = 70;
const CONTEXT_AFTER = 110;

export function articleSnippet(article: HelpArticle, query: string): Snippet | null {
  const needle = query.trim().toLocaleLowerCase("ru");
  if (needle === "") return null;
  const plain = plainText(article.body);
  const at = plain.toLocaleLowerCase("ru").indexOf(needle);
  if (at < 0) return null;

  // Границы двигаются до пробела, чтобы фрагмент не начинался и не заканчивался обрубком слова.
  let from = Math.max(0, at - CONTEXT_BEFORE);
  if (from > 0) {
    const space = plain.indexOf(" ", from);
    from = space >= 0 && space < at ? space + 1 : from;
  }
  let to = Math.min(plain.length, at + needle.length + CONTEXT_AFTER);
  if (to < plain.length) {
    const space = plain.lastIndexOf(" ", to);
    to = space > at + needle.length ? space : to;
  }

  return {
    before: (from > 0 ? "… " : "") + plain.slice(from, at),
    match: plain.slice(at, at + needle.length),
    after: plain.slice(at + needle.length, to) + (to < plain.length ? " …" : ""),
  };
}
